// StatisticsView.tsx
// Created by Taulant Geci

import React, { useState, useEffect } from 'react';
import { MatchGeneratorController } from '../modules/MatchGeneratorController';
import { PlayerStatistics, SortOrder, StatisticsFilter, FilteredStatistics } from '../models/types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, 
  faTrophy, 
  faTimesCircle, 
  faPercent, 
  faHashtag, 
  faUser, 
  faSortUp, 
  faSortDown,
  faUserSlash,
  faMedal,
  faAward,
  faFilter,
  faChevronDown,
  faCalendar,
  faUsers,
  faTimes,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';

interface StatisticsViewProps {
  controller: MatchGeneratorController;
}

export const StatisticsView: React.FC<StatisticsViewProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.WIN_RATE);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');

  useEffect(() => {
    const unsubscribe = controller.subscribe(setState);
    return unsubscribe;
  }, [controller]);

  const filteredStats = controller.getFilteredStatistics();
  const sortedStatistics = controller.getFilteredPlayerStatistics(sortOrder);

  const getSortIcon = (order: SortOrder) => {
    return sortOrder === order ? faSortUp : faSortDown;
  };

  const getSortOrderIcon = (order: SortOrder) => {
    switch (order) {
      case SortOrder.WINS:
        return faTrophy;
      case SortOrder.LOSSES:
        return faTimesCircle;
      case SortOrder.WIN_RATE:
        return faPercent;
      case SortOrder.TOTAL_MATCHES:
        return faHashtag;
      case SortOrder.NAME:
        return faUser;
      default:
        return faPercent;
    }
  };

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <FontAwesomeIcon icon={faMedal} style={{ color: '#ffd700' }} />;
      case 2:
        return <FontAwesomeIcon icon={faMedal} style={{ color: '#c0c0c0' }} />;
      case 3:
        return <FontAwesomeIcon icon={faMedal} style={{ color: '#cd7f32' }} />;
      default:
        return <span className="position-number">{position}</span>;
    }
  };

  const getWinRateColor = (stats: PlayerStatistics) => {
    if (stats.totalMatches === 0) return '#6c757d';
    if (stats.winRate >= 0.7) return '#73897c';
    if (stats.winRate >= 0.5) return '#ffc107';
    return '#ff453a';
  };

  const formatWinRate = (stats: PlayerStatistics) => {
    if (stats.totalMatches === 0) return '-';
    return `${Math.round(stats.winRate * 100)}%`;
  };

  const getSortOrderLabel = (order: SortOrder) => {
    switch (order) {
      case SortOrder.WINS:
        return t('wins');
      case SortOrder.LOSSES:
        return t('losses');
      case SortOrder.WIN_RATE:
        return t('winRate');
      case SortOrder.TOTAL_MATCHES:
        return t('matches');
      case SortOrder.NAME:
        return t('name');
      default:
        return t('winRate');
    }
  };

  const handleGroupFilter = (groupId: string | undefined) => {
    controller.setStatisticsFilter({ groupId });
  };

  const handleDateFilter = () => {
    const updates: Partial<StatisticsFilter> = {
      showAllTime: false
    };
    
    if (tempDateFrom) {
      updates.dateFrom = new Date(tempDateFrom);
    }
    if (tempDateTo) {
      updates.dateTo = new Date(tempDateTo);
    }
    
    controller.setStatisticsFilter(updates);
    setShowAdvancedFilters(false);
  };

  const clearFilters = () => {
    controller.setStatisticsFilter({
      groupId: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      showAllTime: true
    });
    setTempDateFrom('');
    setTempDateTo('');
  };

  const handleTodayFilter = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    controller.setStatisticsFilter({
      showAllTime: false,
      dateFrom: today,
      dateTo: today
    });
    setTempDateFrom(todayStr);
    setTempDateTo(todayStr);
    setShowAdvancedFilters(false);
  };

  const hasActiveFilters = () => {
    const filter = state.statisticsFilter;
    return filter.groupId || !filter.showAllTime;
  };

  if (sortedStatistics.length === 0 && !hasActiveFilters()) {
    return (
      <div className="statistics-view">
        <div className="view-header">
          <h1 className="view-title">
            <FontAwesomeIcon icon={faChartBar} className="me-2" />
            {t('statistics')}
          </h1>
        </div>

        <div className="main-content">
          <div className="empty-state">
            <FontAwesomeIcon icon={faUserSlash} size="3x" className="empty-icon" />
            <h3>{t('noStatisticsAvailable')}</h3>
            <p>{t('playMatchesForStats')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-view">
      <div className="view-header">
        <h1 className="view-title">
          <FontAwesomeIcon icon={faChartBar} className="me-2" />
          {t('statistics')}
        </h1>
        
        <div className="statistics-controls">
          {/* Today Quick Filter Button */}
          <button
            className="today-filter-btn"
            onClick={handleTodayFilter}
            title={t('todayFilter') as string}
          >
            <FontAwesomeIcon icon={faCalendar} className="me-2" />
            {t('todayFilter')}
          </button>
          
          {/* Advanced Filters Button */}
          <button
            className={`advanced-filter-btn ${hasActiveFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title={t('filterStatistics') as string}
          >
            <FontAwesomeIcon icon={faFilter} />
            {hasActiveFilters() && <span className="filter-indicator" />}
          </button>
          
          {/* Sort Dropdown */}
          <div className="filter-dropdown">
            <button
              className="filter-toggle-btn"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              title={t('changeSorting') as string}
            >
              <FontAwesomeIcon icon={getSortOrderIcon(sortOrder)} />
              <FontAwesomeIcon 
                icon={faChevronDown} 
                className={`chevron ${showFilterDropdown ? 'open' : ''}`}
              />
            </button>
            
            {showFilterDropdown && (
              <div className="filter-dropdown-menu">
                <div className="filter-dropdown-header">
                  <FontAwesomeIcon icon={faSortUp} className="me-2" />
                  {t('sortBy')}:
                </div>
                {Object.values(SortOrder).map((order) => (
                  <button
                    key={order}
                    className={`filter-dropdown-item ${sortOrder === order ? 'active' : ''}`}
                    onClick={() => {
                      setSortOrder(order);
                      setShowFilterDropdown(false);
                    }}
                  >
                    <FontAwesomeIcon icon={getSortOrderIcon(order)} className="me-2" />
                    {getSortOrderLabel(order)}
                    {sortOrder === order && (
                      <FontAwesomeIcon icon={getSortIcon(order)} className="ms-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          <div className="advanced-filters-content">
            <h4 className="filter-section-title">
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              {t('filterOptions')}
            </h4>
            
            {/* Group Filter */}
            <div className="filter-section">
              <label className="filter-label">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                {t('filterByGroup')}:
              </label>
              <div className="group-filter-buttons">
                <button
                  className={`group-filter-btn ${!state.statisticsFilter.groupId ? 'active' : ''}`}
                  onClick={() => handleGroupFilter(undefined)}
                >
                  <FontAwesomeIcon icon={faGlobe} className="me-2" />
                  {t('allGroups')}
                </button>
                {state.playerGroups.map(group => (
                  <button
                    key={group.id}
                    className={`group-filter-btn ${state.statisticsFilter.groupId === group.id ? 'active' : ''}`}
                    onClick={() => handleGroupFilter(group.id)}
                  >
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    {group.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Filter */}
            <div className="filter-section">
              <label className="filter-label">
                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                {t('filterByDate')}:
              </label>
              <div className="date-filter-controls">
                <div className="date-filter-toggle">
                  <button
                    className={`date-toggle-btn ${state.statisticsFilter.showAllTime ? 'active' : ''}`}
                    onClick={() => controller.setStatisticsFilter({ showAllTime: true })}
                  >
                    {t('allTime')}
                  </button>
                  <button
                    className={`date-toggle-btn ${!state.statisticsFilter.showAllTime ? 'active' : ''}`}
                    onClick={() => controller.setStatisticsFilter({ showAllTime: false })}
                  >
                    {t('dateRange')}
                  </button>
                </div>
                
                {!state.statisticsFilter.showAllTime && (
                  <div className="date-range-inputs">
                    <div className="date-input-group">
                      <label>{t('from')}:</label>
                      <input
                        type="date"
                        value={tempDateFrom}
                        onChange={(e) => setTempDateFrom(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-input-group">
                      <label>{t('to')}:</label>
                      <input
                        type="date"
                        value={tempDateTo}
                        onChange={(e) => setTempDateTo(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <button
                      className="apply-date-btn"
                      onClick={handleDateFilter}
                      disabled={!tempDateFrom && !tempDateTo}
                    >
                      {t('apply')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters() && (
              <div className="filter-section">
                <button className="clear-filters-btn" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  {t('clearFilters')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Summary */}
      {hasActiveFilters() && (
        <div className="filter-summary">
          <div className="filter-summary-content">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            <span className="filter-summary-text">
              {filteredStats.groupName && (
                <span className="filter-tag">
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  {filteredStats.groupName}
                </span>
              )}
              <span className="filter-tag">
                <FontAwesomeIcon icon={faCalendar} className="me-1" />
                {filteredStats.dateRange}
              </span>
              <span className="filter-tag">
                <FontAwesomeIcon icon={faHashtag} className="me-1" />
                {filteredStats.totalMatches} {t('matches')}
              </span>
            </span>
          </div>
        </div>
      )}

      <div className="main-content">
        {sortedStatistics.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faUserSlash} size="3x" className="empty-icon" />
            <h3>{t('noStatisticsForFilter')}</h3>
            <p>{t('tryDifferentFilter')}</p>
          </div>
        ) : (
          /* Statistics Table */
          <div className="statistics-table-wrapper">
            <table className="statistics-table">
              <thead>
                <tr>
                  <th className="player-col">{t('players')}</th>
                  <th className="winrate-col">
                    <FontAwesomeIcon icon={faPercent} className="me-1" />
                    {t('winRate')}
                  </th>
                  <th className="matches-col">
                    <FontAwesomeIcon icon={faHashtag} className="me-1" />
                    {t('matches')}
                  </th>
                  <th className="wins-col">
                    <FontAwesomeIcon icon={faTrophy} className="me-1" />
                    {t('wins')}
                  </th>
                  <th className="losses-col">
                    <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                    {t('losses')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStatistics.map((stats, index) => (
                  <tr key={stats.playerId} className="statistics-row">
                    <td className="player-cell">
                      <div className="player-info">
                        <div className="player-position">
                          {getPositionIcon(index + 1)}
                        </div>
                        <div className="player-name">
                          {stats.playerName}
                        </div>
                      </div>
                    </td>
                    <td className="winrate-cell">
                      <div 
                        className="win-rate-badge"
                        style={{ backgroundColor: getWinRateColor(stats) }}
                      >
                        {formatWinRate(stats)}
                      </div>
                    </td>
                    <td className="matches-cell">{stats.totalMatches}</td>
                    <td className="wins-cell">
                      <span className="stat-value">{stats.wins}</span>
                    </td>
                    <td className="losses-cell">
                      <span className="stat-value">{stats.losses}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}; 